import { useQuery } from "@tanstack/react-query";
import {
  Card,
  Text,
  Badge,
  Spinner,
  makeStyles,
  tokens,
  Divider,
} from "@fluentui/react-components";
import {
  Document24Regular,
  Mail24Regular,
  People24Regular,
  CheckmarkCircle24Regular,
  ArrowRight16Regular,
  Warning16Regular,
} from "@fluentui/react-icons";
import { useAuth } from "@/auth/AuthContext";
import { getBriefing } from "@/services/api";
import type { BriefingData, DecisionItem, ProjectSummary } from "@/types";

const useStyles = makeStyles({
  root: {
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px",
  },
  kpiCard: {
    padding: "10px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    textAlign: "center" as const,
  },
  kpiIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  kpiValue: {
    fontWeight: 700,
    lineHeight: 1,
  },
  kpiLabel: {
    color: tokens.colorNeutralForeground3,
    lineHeight: 1,
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: "4px",
  },
  decisionCard: {
    padding: "10px 12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  projectCard: {
    padding: "10px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  progressBar: {
    height: "4px",
    borderRadius: "2px",
    backgroundColor: tokens.colorNeutralBackground5,
    marginTop: "4px",
  },
  progressFill: {
    height: "100%",
    borderRadius: "2px",
    background: "linear-gradient(90deg, #6366F1, #8B5CF6)",
  },
  emptyState: {
    textAlign: "center" as const,
    padding: "24px",
    color: tokens.colorNeutralForeground3,
  },
});

export function CommandCenter() {
  const styles = useStyles();
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["briefing"],
    queryFn: () => getBriefing(user!.accessToken),
    enabled: !!user,
  });

  if (isLoading) {
    return (
      <div className={styles.emptyState}>
        <Spinner size="small" label="Loading updates..." />
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.emptyState}>
        <Text size={200}>No data available</Text>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* KPI Cards */}
      <div className={styles.kpiRow}>
        <KpiCard
          icon={<Document24Regular />}
          value={data.kpis.documentsProcessed}
          label="Documents"
          color="#6366F1"
        />
        <KpiCard
          icon={<Mail24Regular />}
          value={data.kpis.rfqsSent}
          label="RFQs Sent"
          color="#8B5CF6"
        />
        <KpiCard
          icon={<CheckmarkCircle24Regular />}
          value={data.kpis.quotesReceived}
          label="Quotes"
          color="#059669"
        />
        <KpiCard
          icon={<People24Regular />}
          value={data.kpis.vendorsMatched}
          label="Vendors"
          color="#0078D4"
        />
      </div>

      {/* Projects */}
      <Divider />
      <Text className={styles.sectionTitle} size={300}>
        Active Projects
      </Text>
      {data.projects.length === 0 ? (
        <div className={styles.emptyState}>
          <Text size={200}>No active projects</Text>
        </div>
      ) : (
        data.projects.slice(0, 8).map((project) => (
          <Card key={project.id} className={styles.projectCard}>
            <div style={{ flex: 1 }}>
              <Text weight="semibold" size={200}>
                {project.name}
              </Text>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${getPipelineProgress(project)}%` }}
                />
              </div>
            </div>
            <Badge
              appearance="outline"
              size="small"
              color={
                project.pipelineStage === "evaluation"
                  ? "success"
                  : project.pipelineStage === "rfq"
                    ? "informative"
                    : "warning"
              }
            >
              {project.pipelineStage}
            </Badge>
          </Card>
        ))
      )}

      {/* Decision Queue */}
      {data.decisionItems.length > 0 && (
        <>
          <Divider />
          <Text className={styles.sectionTitle} size={300}>
            Needs Attention
          </Text>
          {data.decisionItems.slice(0, 5).map((item) => (
            <DecisionCard key={item.id} item={item} />
          ))}
        </>
      )}
    </div>
  );
}

// --- Sub-components ---

function KpiCard({
  icon,
  value,
  label,
  color,
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
  color: string;
}) {
  const styles = useStyles();

  return (
    <Card className={styles.kpiCard}>
      <div
        className={styles.kpiIcon}
        style={{ backgroundColor: `${color}15`, color }}
      >
        {icon}
      </div>
      <div>
        <Text className={styles.kpiValue} size={500}>
          {value}
        </Text>
        <Text className={styles.kpiLabel} size={100} block>
          {label}
        </Text>
      </div>
    </Card>
  );
}

const STAGE_ORDER = ["intake", "scope", "discovery", "rfq", "evaluation"] as const;

function getPipelineProgress(project: ProjectSummary): number {
  const idx = STAGE_ORDER.indexOf(project.pipelineStage);
  if (idx === -1) return 0;
  return Math.round(((idx + 1) / STAGE_ORDER.length) * 100);
}

function DecisionCard({ item }: { item: DecisionItem }) {
  const styles = useStyles();

  return (
    <Card className={styles.decisionCard}>
      <Warning16Regular
        style={{
          color: item.urgency === "critical"
            ? tokens.colorPaletteRedForeground1
            : tokens.colorPaletteYellowForeground1,
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text size={200} weight="semibold" truncate>
          {item.title}
        </Text>
        <Text
          size={100}
          style={{ color: tokens.colorNeutralForeground3 }}
          truncate
          block
        >
          {item.source}
        </Text>
      </div>
      <ArrowRight16Regular
        style={{ color: tokens.colorNeutralForeground3, flexShrink: 0 }}
      />
    </Card>
  );
}
